import { useState, useRef, useCallback } from 'react';
import ReactFlow, { Controls, Background, MiniMap } from 'reactflow';
import { useStore } from './store';
import { shallow } from 'zustand/shallow';

import { InputNode }     from './nodes/inputNode';
import { LLMNode }       from './nodes/llmNode';
import { OutputNode }    from './nodes/outputNode';
import { TextNode }      from './nodes/textNode';
import { FilterNode }    from './nodes/filterNode';
import { TransformNode } from './nodes/transformNode';
import { ConditionNode } from './nodes/conditionNode';
import { NoteNode }      from './nodes/noteNode';
import { ApiNode }       from './nodes/apiNode';

import 'reactflow/dist/style.css';

const gridSize = 20;
const proOptions = { hideAttribution: true };

// All 9 node types registered here
const nodeTypes = {
  customInput:  InputNode,
  llm:          LLMNode,
  customOutput: OutputNode,
  text:         TextNode,
  filter:       FilterNode,
  transform:    TransformNode,
  condition:    ConditionNode,
  note:         NoteNode,
  api:          ApiNode,
};

const selector = (state) => ({
  nodes:         state.nodes,
  edges:         state.edges,
  getNodeID:     state.getNodeID,
  addNode:       state.addNode,
  onNodesChange: state.onNodesChange,
  onEdgesChange: state.onEdgesChange,
  onConnect:     state.onConnect,
});

export const PipelineUI = () => {
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const {
    nodes, edges, getNodeID, addNode,
    onNodesChange, onEdgesChange, onConnect,
  } = useStore(selector, shallow);

  const getInitNodeData = (nodeID, type) => ({ id: nodeID, nodeType: type });

  const onDrop = useCallback((event) => {
    event.preventDefault();
    const bounds = reactFlowWrapper.current.getBoundingClientRect();
    const raw = event?.dataTransfer?.getData('application/reactflow');
    if (!raw) return;

    const { nodeType: type } = JSON.parse(raw);
    if (!type) return;

    const position = reactFlowInstance.project({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    });

    const nodeID = getNodeID(type);
    addNode({ id: nodeID, type, position, data: getInitNodeData(nodeID, type) });
  }, [reactFlowInstance, getNodeID, addNode]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const isValidConnection = useCallback((connection) => {
    // 1. Prevent connecting a node to itself
    if (connection.source === connection.target) return false;

    // 2. Prevent a target handle from holding more than 1 edge connection simultaneously
    const isTargetOccupied = edges.some(
      (edge) => edge.target === connection.target && edge.targetHandle === connection.targetHandle
    );
    
    if (isTargetOccupied) return false;

    return true;
  }, [edges]);

  return (
    <div ref={reactFlowWrapper} style={{ width: '100vw', height: 'calc(100vh - 112px)' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onInit={setReactFlowInstance}
        nodeTypes={nodeTypes}
        proOptions={proOptions}
        snapGrid={[gridSize, gridSize]}
        connectionLineType="smoothstep"
        snapToGrid={true}
        connectionLineStyle={{ stroke: '#6366f1', strokeWidth: 2 }}
        defaultEdgeOptions={{ type: 'smoothstep', animated: true }}
        isValidConnection={isValidConnection} // <-- INJECT VALIDATOR HOOK HERE
        deleteKeyCode={['Backspace', 'Delete']}
        fitView
      >
        <Background color="#cbd5e1" gap={gridSize} size={1} />
        <Controls style={{ bottom: 80 }} />
        <MiniMap
          nodeColor={(n) => {
            const colors = {
              customInput: '#6366f1', llm: '#8b5cf6', customOutput: '#10b981',
              text: '#f59e0b', filter: '#ef4444', transform: '#0ea5e9',
              condition: '#f97316', note: '#84cc16', api: '#06b6d4',
            };
            return colors[n.type] || '#94a3b8';
          }}
          style={{ bottom: 80, right: 8 }}
        />
      </ReactFlow>
    </div>
  );
};